/**
 * Role taxonomy + permission matrix for the per-property tenancy.
 *
 * A membership row carries exactly one role. Capabilities flow entirely
 * from the per-action permission matrix below — there's deliberately no
 * cross-role "rank" or "hierarchy" concept. The only special case is that
 * Owner rows can only be mutated by another Owner, encoded as a single
 * `members:manage-owner` permission rather than a comparison helper.
 *
 * Permissions are tagged strings — keep them in this file so the type
 * union is the single source of truth and TypeScript catches typos at the
 * call sites.
 */

import { z } from "zod";

export const ROLES = ["owner", "manager", "receptionist", "operator"] as const;

export type Role = (typeof ROLES)[number];

export const RoleSchema = z.enum(ROLES);

/** Display label for each role (UI + invitation copy). */
export const ROLE_LABELS: Record<Role, string> = {
  manager: "Manager",
  operator: "Operator",
  owner: "Owner",
  receptionist: "Receptionist",
};

const PERMISSIONS = [
  // Property admin
  "property:settings:edit",
  "property:delete",
  "property:billing",
  // Members
  "members:invite",
  "members:change-role",
  "members:remove",
  // Members — special case: only Owner can mutate an Owner row. Anyone
  // with `members:change-role` / `members:remove` can mutate non-Owner
  // rows; this additional permission gates the Owner-vs-Owner carve-out.
  "members:manage-owner",
  // Reservations
  "reservations:view-all",
  "reservations:view-linked",
  "reservations:create",
  "reservations:edit",
  "reservations:cancel",
  // Tasks
  "tasks:view-all",
  "tasks:view-own",
  "tasks:create",
  "tasks:assign",
  // Change a task's status (complete / reopen / pause / resume). "Own vs all"
  // is a data scope, not a permission: the PowerSync sync rules already only
  // deliver an operator their own assigned tasks, so this single permission
  // covers both. Distinct from `tasks:assign` — an operator can change their
  // task's status but not reassign it.
  "tasks:update",
  // Remove a task that should never have existed (a mistype, a duplicate). A
  // soft delete: the row keeps its history and simply stops syncing. Deliberately
  // NOT held by receptionist or operator, who create and work tasks all day —
  // erasing one is a supervisory act, and completing it is the normal way to
  // close work out.
  "tasks:delete",
  // Staff availability — record time off / availability windows /
  // mode for any staffer. The manager owns every availability change; there
  // is no staff self-serve. Flows to Owner + Manager only (not Receptionist:
  // they assign tasks and see availability, but staffing is a manager job).
  "staff:manage-availability",
  // Staff task functions — tag a staffer with the work they are qualified to
  // do (Cleaning, Maintenance, …). Separate from `staff:manage-availability`
  // because it answers a different question — what someone CAN do, not when
  // they are around — and a future build may well let a staffer claim their
  // own skills while still never setting their own time off. Same reach for
  // now: Owner + Manager, staffing being a manager job.
  "staff:manage-functions",
  // Guests
  "guests:view",
  "guests:edit",
  // Guest chats
  "guest-chats:read",
  "guest-chats:reply",
  // Staff chats
  "staff-chats:participate",
  // Delete ANOTHER member's staff message. Deleting your own needs no
  // permission — authorship is the bar, the same owner-scoped rule reactions
  // use — so this is only the moderator half of "author or manager". Every
  // deletion is a soft delete: the row is retained and the thread renders a
  // tombstone, so this grants tidying a thread, not destroying its history.
  "staff-chats:delete",
  // Meals — mark a morning served or skipped, and move it between the
  // breakfast area and the room. The Operator's second write capability, for
  // the same reason as `tasks:update`: the person plating eleven breakfasts is
  // often field staff, and the sync rules already hand them a money-free
  // projection of the morning. Distinct from `reservations:edit`, which is
  // what CREATING or pricing a meal costs — an operator works the service, a
  // manager decides what is sold.
  "meals:serve",
  // Calendar
  "calendar:view-all",
  "calendar:view-own",
  // Rates: set what a night costs and the rules on it (the Rates view, the
  // base rate and seasons in Settings). A pricing decision, so Owner and
  // Manager only: the front desk sells nights, it does not price them.
  "rates:edit",
  // The lowest price a unit type may sell at. Owner only: it is the guard
  // against a price set too low by anyone else who prices, the Manager
  // included, so the Manager cannot move it.
  "rates:set-floor",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Permissions granted to each role.
 *
 * - Owner is the superset — spread `PERMISSIONS` so new permissions added
 *   to the union automatically flow to Owner without a separate edit.
 * - Manager is "Owner minus the irreversible org-level actions"
 *   (delete property, edit billing, mutate Owner rows).
 * - Receptionist is the operational staff bar (full guest + task + chat
 *   surface; no admin powers).
 * - Operator is field staff — assigned tasks + the staff chats they
 *   participate in, nothing else.
 */
const MANAGER_EXCLUSIONS: readonly Permission[] = [
  "property:delete",
  "property:billing",
  "members:manage-owner",
  "rates:set-floor",
];

const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  manager: PERMISSIONS.filter(
    (p): p is Permission => !MANAGER_EXCLUSIONS.includes(p)
  ),
  operator: [
    "reservations:view-linked",
    "tasks:view-own",
    "tasks:update",
    "meals:serve",
    "staff-chats:participate",
    "calendar:view-own",
  ],
  owner: [...PERMISSIONS],
  receptionist: [
    "reservations:view-all",
    "reservations:view-linked",
    "reservations:create",
    "reservations:edit",
    "reservations:cancel",
    "tasks:view-all",
    "tasks:view-own",
    "tasks:create",
    "tasks:assign",
    "tasks:update",
    "meals:serve",
    "guests:view",
    "guests:edit",
    "guest-chats:read",
    "guest-chats:reply",
    "staff-chats:participate",
    "calendar:view-all",
    "calendar:view-own",
  ],
};

/** True when the given role has the named permission. */
export function roleHasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/**
 * Narrow an opaque membership role string to a known `Role`.
 *
 * `roleHasPermission` indexes `ROLE_PERMISSIONS` and throws on an unknown key,
 * so a role arriving as a bare string (a session's `memberRole`) must pass
 * through here first.
 */
export function isRole(value: string | null | undefined): value is Role {
  return (
    value !== null &&
    value !== undefined &&
    (ROLES as readonly string[]).includes(value)
  );
}

/**
 * True when `actor` may mutate (change role / remove) a member at
 * `target`'s role. The only carve-out is the Owner-vs-Owner case:
 * mutating an Owner row requires `members:manage-owner`, which only
 * Owner has. For non-Owner targets, the actor's per-action permission
 * (`members:change-role` / `members:remove`) is the sole gate — call
 * sites should check that separately.
 */
export function canManageMember(actor: Role, target: Role): boolean {
  if (target === "owner") {
    return roleHasPermission(actor, "members:manage-owner");
  }
  return true;
}

/**
 * The roles `actor` may hand out — when inviting a member, or when changing
 * one's role.
 *
 * An unknown actor gets nothing. A caller reaches this with a null role while
 * the membership query is still in flight, and the two answers there are not
 * symmetric: showing an empty picker for a moment costs a beat, while
 * defaulting to "everything" offers Owner to a Receptionist and relies on
 * whatever the caller happens to do with the result.
 */
export function assignableRoles(actor: Role | null | undefined): Role[] {
  if (!actor) {
    return [];
  }
  return ROLES.filter((target) => canManageMember(actor, target));
}
